/**
 * ConversationManager — coordinates MLS state across the server boundary.
 *
 * Responsibilities:
 * - Maintain a live `ClientState` per conversation (in memory + KeyStore persistence)
 * - Create new conversations (MLS groups)
 * - Invite a user by fetching their KeyPackage from the directory and posting
 *   a Welcome through the invitation inbox
 * - Accept pending invitations from the inbox
 * - Encrypt and decrypt application messages
 *
 * The server never sees MLS secrets. Every operation below either runs on
 * opaque ciphertext or on public MLS routing data (ratchet tree, public
 * KeyPackages).
 */
import {
  acceptWelcome,
  addMember,
  memberIdentities,
  memberLeafIndex,
  removeMember,
  createGroup,
  createKeyPackage,
  decryptMessage,
  encryptMessage,
  resolveCiphersuite,
  type KeyPackageBundle,
} from '@chat/crypto';
import type { KeyStore } from '@chat/crypto';
import {
  fromBase64Url,
  toBase64Url,
  type ConversationId,
  type DeviceRegistration,
  type Invitation,
  type PublishedKeyPackage,
  type UserId,
} from '@chat/protocol';
import {
  decodeGroupState,
  decodeMlsMessage,
  encodeGroupState,
  encodeMlsMessage,
  type CiphersuiteImpl,
  type ClientState,
  type RatchetTree,
} from 'ts-mls';
import { defaultClientConfig } from 'ts-mls/clientConfig.js';
import { decodeRatchetTree, encodeRatchetTree } from 'ts-mls/ratchetTree.js';
import type { Identity } from './identity.js';
import type { ChatClient } from './api.js';
import { writeMutedConversations } from './mute-storage.js';

const STATE_PREFIX = 'chat:conversation:v1:';
const KEYPACKAGE_CACHE_PREFIX = 'chat:kp-cache:v1:';
const META_PREFIX = 'chat:conversation-meta:v1:';
const MESSAGE_PREFIX = 'chat:msg:v1:';
const SEQ_PREFIX = 'chat:seq:v1:';
const GAME_SCORE_PREFIX = 'chat:game-score:v1:';
const BLOCK_PREFIX = 'chat:block:v1:';
const DRAFT_PREFIX = 'chat:draft:v1:';

function metaKey(id: ConversationId): string {
  return `${META_PREFIX}${id}`;
}

function messagePrefix(id: ConversationId): string {
  return `${MESSAGE_PREFIX}${id}:`;
}

function messageKey(id: ConversationId, sequence: number): string {
  return `${messagePrefix(id)}${sequence.toString().padStart(12, '0')}`;
}

function seqKey(id: ConversationId): string {
  return `${SEQ_PREFIX}${id}`;
}

function gameScoreKey(id: ConversationId, engineId: string): string {
  return `${GAME_SCORE_PREFIX}${id}:${engineId}`;
}

export interface GameScore {
  winsByUser: Record<string, number>;
  draws: number;
}

/**
 * Persisted message record. We keep the decoded ChatPayload (JSON) rather
 * than ciphertext — MLS already ratcheted past this message so we can't
 * decrypt again from history. Storing the plaintext locally (sealed under
 * the user's passphrase via SealedKeyStore) means reload no longer drops
 * the visible conversation.
 *
 * Attachments persist as references only; the blob download + object-URL
 * is rebuilt at render time.
 */
/** Reactions aggregated per-message: `{ "👍": [userId1, userId2], ... }`. */
export type ReactionMap = Record<string, UserId[]>;

export interface StoredMessage {
  readonly conversationId: ConversationId;
  readonly sequence: number;
  readonly direction: 'sent' | 'received';
  readonly at: number;
  readonly senderUserId?: UserId;
  /** Decoded ChatPayload, stored as its JSON-plain representation. */
  readonly payload: unknown;
  /**
   * Reactions applied to this message, aggregated across every
   * received reaction envelope. Mutated in-place when new reactions
   * arrive; absent means no reactions yet.
   */
  reactions?: ReactionMap;
  /**
   * Marked true when a deletion envelope from the original sender
   * lands. The bubble renders as a tombstone but stays in the list
   * so sequence numbers don't shift.
   */
  deleted?: true;
}

export interface ConversationMeta {
  /** User-chosen display name. Falls back to a member-derived label in the UI. */
  name?: string;
  /** Client-tracked member list. The DO is authoritative server-side; this is the view used for rendering. */
  members: UserId[];
  /** ms-since-epoch of the last rename/member-change, for cache invalidation. */
  updatedAt: number;
  /**
   * Highest server-sequence we've stored locally for this conversation
   * (both sent and received). Paired with readUpToSequence to compute
   * unread counts in the sidebar — sync, no storage read per render.
   */
  latestSequence?: number;
  /** Highest server-sequence the user has marked as read. */
  readUpToSequence?: number;
  /**
   * User has muted this conversation locally — the sidebar shows a
   * muted badge and the push service worker can be wired to skip
   * system notifications for its id. Purely a per-device flag; no
   * server-side state.
   */
  muted?: true;
  /**
   * User has pinned this conversation locally so it stays at the
   * top of the sidebar list regardless of recent activity.
   */
  pinned?: true;
  /**
   * Server sequences of messages pinned to this conversation by
   * any member. Sorted ascending; capped to a small number client-
   * side so a runaway pin spam doesn't bloat meta. Empty / absent
   * means no pins.
   */
  pinnedSequences?: number[];
  /**
   * Shared group avatar — set by any member, broadcast via the
   * groupAvatar payload, persisted into local meta. Stored as
   * `{ avatarMediaId, uploaderUserId }` because the avatar GET
   * path is keyed by uploader.
   */
  groupAvatar?: { avatarMediaId: string; uploaderUserId: UserId };
}

/** Maximum number of messages a single conversation can keep pinned. */
export const MAX_PINNED_MESSAGES = 20;

/** Cap on distinct emoji per message — V5-MEDIUM-02. */
export const MAX_DISTINCT_REACTIONS_PER_MESSAGE = 32;

function stateKey(id: ConversationId): string {
  return `${STATE_PREFIX}${id}`;
}

function keyPackageCacheKey(conversationId: ConversationId, leafIndex: number): string {
  return `${KEYPACKAGE_CACHE_PREFIX}${conversationId}:${leafIndex.toString().padStart(6, '0')}`;
}

/**
 * Derive a user-visible conversation id from an MLS group id. We base64url-
 * encode the group id and slap a prefix on it so it satisfies the protocol's
 * ConversationId shape (8-128 chars, URL-safe).
 */
function conversationIdFromGroup(groupId: Uint8Array): ConversationId {
  return `conv-${toBase64Url(groupId).slice(0, 48)}` as ConversationId;
}

function randomGroupId(): Uint8Array {
  const id = new Uint8Array(32);
  crypto.getRandomValues(id);
  return id;
}

interface PersistedConversation {
  groupId: string;
  conversationId: ConversationId;
  groupState: string;
  keyPackage: string;
  privatePackage: {
    initPrivateKey: string;
    hpkePrivateKey: string;
    signaturePrivateKey: string;
  };
}

function encodeKeyPackage(bundle: KeyPackageBundle): string {
  return toBase64Url(
    encodeMlsMessage({
      keyPackage: bundle.publicPackage,
      wireformat: 'mls_key_package',
      version: 'mls10',
    }),
  );
}

function decodeKeyPackageBundle(
  publicSerialized: string,
  priv: PersistedConversation['privatePackage'],
): KeyPackageBundle {
  const decoded = decodeMlsMessage(fromBase64Url(publicSerialized), 0);
  if (!decoded || decoded[0].wireformat !== 'mls_key_package') {
    throw new Error('corrupt persisted key package');
  }
  return {
    publicPackage: decoded[0].keyPackage,
    privatePackage: {
      initPrivateKey: fromBase64Url(priv.initPrivateKey),
      hpkePrivateKey: fromBase64Url(priv.hpkePrivateKey),
      signaturePrivateKey: fromBase64Url(priv.signaturePrivateKey),
    },
  };
}

function encodePrivatePackage(
  priv: KeyPackageBundle['privatePackage'],
): PersistedConversation['privatePackage'] {
  return {
    initPrivateKey: toBase64Url(priv.initPrivateKey),
    hpkePrivateKey: toBase64Url(priv.hpkePrivateKey),
    signaturePrivateKey: toBase64Url(priv.signaturePrivateKey),
  };
}

export interface ConversationManagerDeps {
  readonly identity: Identity;
  readonly client: ChatClient;
  readonly store: KeyStore;
  readonly tokenProvider: () => Promise<string> | string;
  readonly suite?: CiphersuiteImpl;
}

export class ConversationManager {
  readonly #identity: Identity;
  readonly #client: ChatClient;
  readonly #store: KeyStore;
  readonly #getToken: () => Promise<string> | string;
  readonly #suite: CiphersuiteImpl;
  readonly #states = new Map<ConversationId, ClientState>();
  readonly #meta = new Map<ConversationId, ConversationMeta>();
  readonly #metaListeners = new Set<() => void>();
  /**
   * Monotonically increasing counter bumped on every meta change. Used
   * as a `useSyncExternalStore` snapshot so React re-renders pick up
   * rename + member-list updates even when no other state changed.
   */
  #metaVersion = 0;

  private constructor(
    identity: Identity,
    client: ChatClient,
    store: KeyStore,
    getToken: () => Promise<string> | string,
    suite: CiphersuiteImpl,
  ) {
    this.#identity = identity;
    this.#client = client;
    this.#store = store;
    this.#getToken = getToken;
    this.#suite = suite;
  }

  static async create(deps: ConversationManagerDeps): Promise<ConversationManager> {
    const suite = deps.suite ?? (await resolveCiphersuite());
    const manager = new ConversationManager(
      deps.identity,
      deps.client,
      deps.store,
      deps.tokenProvider,
      suite,
    );
    await manager.#hydrate();
    return manager;
  }

  async #hydrate(): Promise<void> {
    const keys = await this.#store.list(STATE_PREFIX);
    for (const key of keys) {
      const raw = await this.#store.get(key);
      if (!raw) continue;
      const persisted = JSON.parse(new TextDecoder().decode(raw)) as PersistedConversation;
      const [groupState] = decodeGroupState(fromBase64Url(persisted.groupState), 0) ?? [undefined];
      if (!groupState) continue;
      const state: ClientState = {
        ...groupState,
        clientConfig: defaultClientConfig,
      };
      this.#states.set(persisted.conversationId, state);
    }
    const metaKeys = await this.#store.list(META_PREFIX);
    for (const key of metaKeys) {
      const raw = await this.#store.get(key);
      if (!raw) continue;
      try {
        const parsed = JSON.parse(new TextDecoder().decode(raw)) as ConversationMeta & {
          conversationId?: ConversationId;
        };
        const id = (parsed.conversationId ??
          (key.startsWith(META_PREFIX) ? key.slice(META_PREFIX.length) : undefined)) as
          | ConversationId
          | undefined;
        if (!id) continue;
        this.#meta.set(id, {
          members: Array.isArray(parsed.members) ? parsed.members : [],
          updatedAt: typeof parsed.updatedAt === 'number' ? parsed.updatedAt : 0,
          ...(parsed.name ? { name: parsed.name } : {}),
          ...(typeof parsed.latestSequence === 'number'
            ? { latestSequence: parsed.latestSequence }
            : {}),
          ...(typeof parsed.readUpToSequence === 'number'
            ? { readUpToSequence: parsed.readUpToSequence }
            : {}),
          ...(parsed.muted ? { muted: true as const } : {}),
          ...(parsed.pinned ? { pinned: true as const } : {}),
          ...(Array.isArray(parsed.pinnedSequences)
            ? {
                pinnedSequences: parsed.pinnedSequences.filter(
                  (s): s is number => typeof s === 'number' && Number.isInteger(s) && s > 0,
                ),
              }
            : {}),
          ...(parsed.groupAvatar &&
          typeof parsed.groupAvatar === 'object' &&
          typeof parsed.groupAvatar.avatarMediaId === 'string' &&
          typeof parsed.groupAvatar.uploaderUserId === 'string'
            ? {
                groupAvatar: {
                  avatarMediaId: parsed.groupAvatar.avatarMediaId,
                  uploaderUserId: parsed.groupAvatar.uploaderUserId,
                },
              }
            : {}),
        });
      } catch {
        // Corrupt metadata — ignore; the conversation still renders with a fallback label.
      }
    }
    // Block list — each entry is keyed by userId; presence = blocked.
    const blockKeys = await this.#store.list(BLOCK_PREFIX);
    for (const key of blockKeys) {
      const userId = key.slice(BLOCK_PREFIX.length) as UserId;
      this.#blocked.add(userId);
    }
    // After meta is populated, push the current muted-list into the
    // plain-IDB pocket the service worker reads on push.
    await this.#syncMutedConversationsIdb();
  }

  #emitMetaChange(): void {
    this.#metaVersion += 1;
    for (const fn of this.#metaListeners) fn();
  }

  metaVersion(): number {
    return this.#metaVersion;
  }

  async #persistMeta(id: ConversationId, meta: ConversationMeta): Promise<void> {
    this.#meta.set(id, meta);
    const payload = new TextEncoder().encode(JSON.stringify({ conversationId: id, ...meta }));
    await this.#store.put(metaKey(id), payload);
    this.#emitMetaChange();
  }

  subscribeMeta(fn: () => void): () => void {
    this.#metaListeners.add(fn);
    return () => {
      this.#metaListeners.delete(fn);
    };
  }

  meta(id: ConversationId): ConversationMeta | undefined {
    return this.#meta.get(id);
  }

  members(id: ConversationId): readonly UserId[] {
    return this.#meta.get(id)?.members ?? [];
  }

  /**
   * Fetch a previously-stored message by (conversation, sequence), or
   * undefined if none was persisted. Used by the reaction / deletion
   * path to mutate the target message in place.
   */
  async getStoredMessage(
    conversationId: ConversationId,
    sequence: number,
  ): Promise<StoredMessage | undefined> {
    const raw = await this.#store.get(messageKey(conversationId, sequence));
    if (!raw) return undefined;
    try {
      return JSON.parse(new TextDecoder().decode(raw)) as StoredMessage;
    } catch {
      return undefined;
    }
  }

  /**
   * Toggle `reactor`'s reaction (`emoji`) on the message at
   * (conversationId, targetSequence). Add if absent, remove if
   * present. Returns the mutated record for the caller to mirror
   * into React state; undefined when the target isn't stored yet.
   * An empty-string emoji clears the reactor's reactions across all
   * emojis on that message (used when the sender untoggles).
   */
  async applyReaction(
    conversationId: ConversationId,
    targetSequence: number,
    reactor: UserId,
    emoji: string,
  ): Promise<StoredMessage | undefined> {
    const target = await this.getStoredMessage(conversationId, targetSequence);
    if (!target) return undefined;
    const reactions: ReactionMap = { ...(target.reactions ?? {}) };
    if (emoji === '') {
      for (const key of Object.keys(reactions)) {
        reactions[key] = (reactions[key] ?? []).filter((u) => u !== reactor);
        if (reactions[key]?.length === 0) delete reactions[key];
      }
    } else {
      const current = reactions[emoji] ?? [];
      if (current.includes(reactor)) {
        const next = current.filter((u) => u !== reactor);
        if (next.length === 0) delete reactions[emoji];
        else reactions[emoji] = next;
      } else {
        // Cap distinct emoji per message to bound sealed-keystore
        // growth when a hostile peer streams reactions with random
        // emoji keys (V5-MEDIUM-02). 32 is well above legitimate use
        // and still room for the QUICK_REACTIONS row plus a handful
        // of custom additions.
        if (Object.keys(reactions).length >= MAX_DISTINCT_REACTIONS_PER_MESSAGE) {
          return target;
        }
        reactions[emoji] = [...current, reactor];
      }
    }
    const hasReactions = Object.keys(reactions).length > 0;
    const rest: StoredMessage = { ...target };
    delete rest.reactions;
    const updated: StoredMessage = hasReactions ? { ...rest, reactions } : rest;
    await this.#store.put(
      messageKey(conversationId, targetSequence),
      new TextEncoder().encode(JSON.stringify(updated)),
    );
    return updated;
  }

  /**
   * Flag the target message as deleted — UI renders a tombstone.
   * Caller MUST have already verified the deletion envelope's sender
   * matches the target's senderUserId; this method trusts that check.
   * Returns the updated record, or undefined if no such target exists.
   */
  async applyDeletion(
    conversationId: ConversationId,
    targetSequence: number,
  ): Promise<StoredMessage | undefined> {
    const target = await this.getStoredMessage(conversationId, targetSequence);
    if (!target) return undefined;
    const updated: StoredMessage = { ...target, deleted: true };
    await this.#store.put(
      messageKey(conversationId, targetSequence),
      new TextEncoder().encode(JSON.stringify(updated)),
    );
    return updated;
  }

  /**
   * Persist a single message in the sealed keystore. Idempotent — two
   * calls with the same (conversationId, sequence) overwrite cleanly.
   */
  async saveMessage(message: StoredMessage): Promise<void> {
    const body = new TextEncoder().encode(JSON.stringify(message));
    await this.#store.put(messageKey(message.conversationId, message.sequence), body);
    const current = await this.getLastSeq(message.conversationId);
    if (message.sequence > current) {
      await this.#store.put(
        seqKey(message.conversationId),
        new TextEncoder().encode(String(message.sequence)),
      );
    }
    // Keep conversation meta's latestSequence in sync so the sidebar
    // unread badge can read it synchronously on every render. Sent
    // messages auto-mark themselves as read since the user clearly
    // just saw what they typed.
    const meta = this.#meta.get(message.conversationId) ?? { members: [], updatedAt: 0 };
    const nextLatest = Math.max(meta.latestSequence ?? 0, message.sequence);
    const nextRead =
      message.direction === 'sent'
        ? Math.max(meta.readUpToSequence ?? 0, message.sequence)
        : meta.readUpToSequence;
    if (nextLatest !== (meta.latestSequence ?? 0) || nextRead !== meta.readUpToSequence) {
      await this.#persistMeta(message.conversationId, {
        ...meta,
        latestSequence: nextLatest,
        ...(nextRead !== undefined ? { readUpToSequence: nextRead } : {}),
      });
    }
  }

  /**
   * Mark everything up to `sequence` (or the current latest if omitted)
   * as read. No-op if the ack is already past that point.
   */
  async markRead(id: ConversationId, sequence?: number): Promise<void> {
    const meta = this.#meta.get(id);
    if (!meta) return;
    const latest = meta.latestSequence ?? 0;
    const target = Math.min(latest, sequence ?? latest);
    const prev = meta.readUpToSequence ?? 0;
    if (target <= prev) return;
    await this.#persistMeta(id, { ...meta, readUpToSequence: target });
  }

  /**
   * Per-(conversation, engine) running score. `winsByUser[userId]` is
   * the number of matches that user has won in this conversation for
   * the given engine; `draws` is the count of draws. Missing users
   * default to 0 wins.
   */
  async loadGameScore(id: ConversationId, engineId: string): Promise<GameScore> {
    const raw = await this.#store.get(gameScoreKey(id, engineId));
    if (!raw) return { winsByUser: {}, draws: 0 };
    try {
      const parsed = JSON.parse(new TextDecoder().decode(raw)) as Partial<GameScore>;
      return {
        winsByUser:
          parsed.winsByUser && typeof parsed.winsByUser === 'object' ? parsed.winsByUser : {},
        draws: typeof parsed.draws === 'number' ? parsed.draws : 0,
      };
    } catch {
      return { winsByUser: {}, draws: 0 };
    }
  }

  /**
   * Record a terminal game outcome. Winner → +1 win for that user;
   * draw → +1 draws; both-null (degenerate) is a no-op. Returns the
   * updated score so the caller can re-render without another load.
   */
  async recordGameResult(
    id: ConversationId,
    engineId: string,
    result: { readonly winner?: UserId; readonly draw?: boolean },
  ): Promise<GameScore> {
    const current = await this.loadGameScore(id, engineId);
    const next: GameScore = {
      winsByUser: { ...current.winsByUser },
      draws: current.draws,
    };
    if (result.draw) {
      next.draws += 1;
    } else if (result.winner) {
      next.winsByUser[result.winner] = (next.winsByUser[result.winner] ?? 0) + 1;
    } else {
      return current;
    }
    await this.#store.put(
      gameScoreKey(id, engineId),
      new TextEncoder().encode(JSON.stringify(next)),
    );
    return next;
  }

  /**
   * How many unread messages the given conversation has, computed
   * synchronously from local meta. Safe to call on every sidebar
   * render; no storage IO.
   */
  unreadCount(id: ConversationId): number {
    const meta = this.#meta.get(id);
    if (!meta) return 0;
    const latest = meta.latestSequence ?? 0;
    const read = meta.readUpToSequence ?? 0;
    return Math.max(0, latest - read);
  }

  /**
   * Per-conversation composer draft persistence. Cheap UX win: a
   * user typing a long message who switches tabs / reloads doesn't
   * lose what they had. Stored sealed with everything else; an
   * empty string clears the entry.
   */
  async loadDraft(id: ConversationId): Promise<string> {
    const raw = await this.#store.get(`${DRAFT_PREFIX}${id}`);
    if (!raw) return '';
    try {
      return new TextDecoder().decode(raw);
    } catch {
      return '';
    }
  }

  async saveDraft(id: ConversationId, text: string): Promise<void> {
    if (text.length === 0) {
      await this.#store.delete(`${DRAFT_PREFIX}${id}`);
      return;
    }
    await this.#store.put(`${DRAFT_PREFIX}${id}`, new TextEncoder().encode(text));
  }

  /**
   * Substring search over persisted plaintext messages across every
   * conversation the user is in. Matches `payload.text` only
   * (attachments' filenames could be added later; game moves and
   * reactions aren't text). Case-insensitive; returns matches sorted
   * newest-first and capped at `limit`.
   *
   * Called from the sidebar search input; no network, no decryption —
   * plaintext lives in the sealed keystore already.
   */
  async searchMessages(
    query: string,
    limit = 50,
  ): Promise<
    {
      conversationId: ConversationId;
      sequence: number;
      at: number;
      direction: 'sent' | 'received';
      senderUserId?: UserId;
      snippet: string;
    }[]
  > {
    const needle = query.trim().toLowerCase();
    if (needle.length === 0) return [];
    const hits: {
      conversationId: ConversationId;
      sequence: number;
      at: number;
      direction: 'sent' | 'received';
      senderUserId?: UserId;
      snippet: string;
    }[] = [];
    for (const cid of this.#states.keys()) {
      const stored = await this.loadMessages(cid);
      for (const m of stored) {
        if (m.deleted) continue;
        const payload = m.payload as { text?: unknown };
        const text = typeof payload.text === 'string' ? payload.text : undefined;
        if (!text) continue;
        if (!text.toLowerCase().includes(needle)) continue;
        const idx = text.toLowerCase().indexOf(needle);
        const start = Math.max(0, idx - 30);
        const end = Math.min(text.length, idx + needle.length + 30);
        const snippet = `${start > 0 ? '…' : ''}${text.slice(start, end)}${
          end < text.length ? '…' : ''
        }`;
        hits.push({
          conversationId: cid,
          sequence: m.sequence,
          at: m.at,
          direction: m.direction,
          ...(m.senderUserId ? { senderUserId: m.senderUserId } : {}),
          snippet,
        });
      }
    }
    hits.sort((a, b) => b.at - a.at);
    return hits.slice(0, limit);
  }

  async loadMessages(id: ConversationId): Promise<StoredMessage[]> {
    const keys = await this.#store.list(messagePrefix(id));
    const out: StoredMessage[] = [];
    for (const key of keys) {
      const raw = await this.#store.get(key);
      if (!raw) continue;
      try {
        out.push(JSON.parse(new TextDecoder().decode(raw)) as StoredMessage);
      } catch {
        // skip corrupt record
      }
    }
    out.sort((a, b) => a.sequence - b.sequence);
    return out;
  }

  async getLastSeq(id: ConversationId): Promise<number> {
    const raw = await this.#store.get(seqKey(id));
    if (!raw) return 0;
    const parsed = Number(new TextDecoder().decode(raw));
    return Number.isFinite(parsed) ? parsed : 0;
  }

  /**
   * In-memory snapshot of the client's block list, populated from the
   * sealed keystore on #hydrate. The sidebar renders its "Blocked"
   * section from this; processOneEnvelope and acceptInvitations
   * consult it for drops.
   */
  readonly #blocked = new Set<UserId>();

  blocked(): readonly UserId[] {
    return [...this.#blocked];
  }

  isBlocked(userId: UserId): boolean {
    return this.#blocked.has(userId);
  }

  async block(userId: UserId): Promise<void> {
    if (this.#blocked.has(userId)) return;
    this.#blocked.add(userId);
    await this.#store.put(`${BLOCK_PREFIX}${userId}`, new Uint8Array([1]));
    this.#emitMetaChange();
  }

  async unblock(userId: UserId): Promise<void> {
    if (!this.#blocked.has(userId)) return;
    this.#blocked.delete(userId);
    await this.#store.delete(`${BLOCK_PREFIX}${userId}`);
    this.#emitMetaChange();
  }

  /**
   * Apply a pin / unpin update to the conversation's persisted set.
   * Capped at MAX_PINNED_MESSAGES; the oldest sequence is dropped
   * if a new pin would exceed the cap. Returns the updated meta.
   */
  async applyPinUpdate(
    id: ConversationId,
    targetSequence: number,
    pinned: boolean,
  ): Promise<ConversationMeta> {
    const existing = this.#meta.get(id) ?? { members: [], updatedAt: 0 };
    // V5-LOW-01: only honour pins that target a real message in
    // this conversation. Hostile peers could otherwise spam pin
    // envelopes with arbitrary sequences and force a sealed-keystore
    // write per envelope. Unpin requests pass through unconditionally
    // — they only ever shrink the set.
    if (pinned) {
      const target = await this.getStoredMessage(id, targetSequence);
      if (!target || target.deleted) return existing;
    }
    const current = new Set(existing.pinnedSequences ?? []);
    if (pinned) current.add(targetSequence);
    else current.delete(targetSequence);
    let next = [...current].sort((a, b) => a - b);
    if (next.length > MAX_PINNED_MESSAGES) {
      next = next.slice(next.length - MAX_PINNED_MESSAGES);
    }
    const meta: ConversationMeta = { ...existing, updatedAt: Date.now() };
    if (next.length > 0) meta.pinnedSequences = next;
    else delete meta.pinnedSequences;
    await this.#persistMeta(id, meta);
    return meta;
  }

  /**
   * Apply a group-rename update to a conversation's persisted meta.
   * `name === null` clears the override and falls back to the
   * member-derived label in the UI; a non-null trimmed name is
   * stored verbatim. Caller (Chat.tsx) is responsible for the
   * length cap during the broadcast — receive-side decode already
   * enforces it via parseChatPayload's GROUP_NAME_MAX_LENGTH.
   */
  async applyGroupNameUpdate(id: ConversationId, name: string | null): Promise<ConversationMeta> {
    const existing = this.#meta.get(id) ?? { members: [], updatedAt: 0 };
    const next: ConversationMeta = { ...existing, updatedAt: Date.now() };
    if (name === null || name.trim() === '') delete next.name;
    else next.name = name.trim();
    await this.#persistMeta(id, next);
    return next;
  }

  /**
   * Apply a group-avatar update to a conversation's persisted meta.
   * `avatarMediaId === null` clears the avatar; any non-null id is
   * stored alongside the uploader so the GET path can resolve.
   */
  async applyGroupAvatarUpdate(
    id: ConversationId,
    avatarMediaId: string | null,
    uploaderUserId: UserId,
  ): Promise<ConversationMeta> {
    const existing = this.#meta.get(id) ?? { members: [], updatedAt: 0 };
    const next: ConversationMeta = { ...existing, updatedAt: Date.now() };
    if (avatarMediaId === null) delete next.groupAvatar;
    else next.groupAvatar = { avatarMediaId, uploaderUserId };
    await this.#persistMeta(id, next);
    return next;
  }

  /**
   * Toggle a conversation's local "muted" flag. Currently affects
   * sidebar appearance only — push mute is a future follow-up that
   * requires coordinating with the service worker via a shared IDB
   * entry.
   */
  async setPinned(id: ConversationId, pinned: boolean): Promise<void> {
    const existing = this.#meta.get(id) ?? { members: [], updatedAt: 0 };
    if (pinned && existing.pinned) return;
    if (!pinned && !existing.pinned) return;
    const next: ConversationMeta = { ...existing, updatedAt: Date.now() };
    if (pinned) next.pinned = true;
    else delete next.pinned;
    await this.#persistMeta(id, next);
  }

  async setMuted(id: ConversationId, muted: boolean): Promise<void> {
    const existing = this.#meta.get(id) ?? { members: [], updatedAt: 0 };
    if (muted && existing.muted) return;
    if (!muted && !existing.muted) return;
    const next: ConversationMeta = { ...existing, updatedAt: Date.now() };
    if (muted) next.muted = true;
    else delete next.muted;
    await this.#persistMeta(id, next);
    await this.#syncMutedConversationsIdb();
  }

  /**
   * Mirror the current muted set into the plain IDB entry sw.js
   * reads on each push. Called whenever setMuted mutates and on
   * boot via #hydrate so reloads don't race.
   */
  async #syncMutedConversationsIdb(): Promise<void> {
    const ids: string[] = [];
    for (const [cid, meta] of this.#meta) {
      if (meta.muted) ids.push(cid);
    }
    await writeMutedConversations(ids);
  }

  async setName(id: ConversationId, name: string | undefined): Promise<void> {
    const existing = this.#meta.get(id) ?? { members: [], updatedAt: 0 };
    const trimmed = name?.trim();
    const next: ConversationMeta = {
      members: existing.members,
      updatedAt: Date.now(),
      ...(trimmed ? { name: trimmed } : {}),
    };
    await this.#persistMeta(id, next);
  }

  conversations(): ConversationId[] {
    return Array.from(this.#states.keys());
  }

  state(id: ConversationId): ClientState | undefined {
    return this.#states.get(id);
  }

  /**
   * Create a brand new group. Returns the conversation id that others
   * will be invited into.
   */
  async newConversation(): Promise<ConversationId> {
    const bundle = await createKeyPackage(this.#identity.userId, this.#suite);
    const groupId = randomGroupId();
    const conversationId = conversationIdFromGroup(groupId);
    const state = await createGroup(groupId, bundle, this.#suite);
    this.#states.set(conversationId, state);
    await this.#persist(conversationId, state, bundle);
    await this.#persistMeta(conversationId, {
      members: [this.#identity.userId],
      updatedAt: Date.now(),
    });
    return conversationId;
  }

  /**
   * Ask the server to wipe the conversation for every member, then
   * clean it up locally. Any member is allowed to do this. Other
   * members find out on their next poll — /history returns 403
   * ("not a conversation member" — the member list is gone with the
   * rest of the DO's storage) and their client drops the local
   * conversation the same way it would for a revoked identity.
   */
  async deleteConversationForEveryone(id: ConversationId): Promise<void> {
    const token = await this.#getToken();
    await this.#client.deleteConversation(token, id);
    await this.removeConversation(id);
  }

  /**
   * Tear down a conversation locally — removes the group state, meta,
   * and any cached per-conversation KeyPackages. Intended for rolling
   * back a just-created group when the follow-up invite failed (e.g.
   * the invitee had no KeyPackages on the directory). Does NOT tell
   * the server to delete the DO storage; that's fine because the DO
   * only holds members + message history for groups the user is in,
   * and an orphan group stays dormant + empty.
   */
  async removeConversation(id: ConversationId): Promise<void> {
    this.#states.delete(id);
    this.#meta.delete(id);
    await this.#store.delete(stateKey(id));
    await this.#store.delete(metaKey(id));
    await this.#store.delete(seqKey(id));
    const cachedKps = await this.#store.list(`${KEYPACKAGE_CACHE_PREFIX}${id}:`);
    for (const k of cachedKps) await this.#store.delete(k);
    const msgs = await this.#store.list(messagePrefix(id));
    for (const k of msgs) await this.#store.delete(k);
    this.#emitMetaChange();
  }

  /**
   * Fetches the target's KeyPackage, adds them to the group, and drops
   * the resulting Welcome into the server's invitation inbox. Returns the
   * number of members now in the group.
   */
  async invite(conversationId: ConversationId, targetUserId: UserId): Promise<void> {
    const state = this.#mustHaveState(conversationId);
    const token = await this.#getToken();
    const published = await this.#client.takeKeyPackage(token, targetUserId);
    if (!published) {
      throw new Error(`no key package available for ${targetUserId}`);
    }
    const decoded = decodeMlsMessage(fromBase64Url(published.keyPackage), 0);
    if (!decoded || decoded[0].wireformat !== 'mls_key_package') {
      throw new Error('invalid key package from directory');
    }
    const result = await addMember(state, decoded[0].keyPackage, this.#suite);
    this.#states.set(conversationId, result.newState);
    await this.#persistStateOnly(conversationId, result.newState);

    const invitation: Invitation = {
      fromUserId: this.#identity.userId,
      toUserId: targetUserId,
      conversationId,
      welcome: toBase64Url(result.welcome),
      ratchetTree: toBase64Url(encodeRatchetTree(result.newState.ratchetTree)),
      createdAt: Date.now(),
    };
    await this.#client.sendInvitation(token, invitation);

    // Broadcast the MLS Commit to existing group members over the
    // regular /send channel. Without this every other member stays
    // at the pre-invite epoch and silently fails to decrypt future
    // application messages. The worker/DO doesn't care that the
    // ciphertext is a handshake rather than an application payload
    // — both ride the same mls_private_message wire format, and
    // recipients route by kind in decryptMessage.
    const peers = (this.#meta.get(conversationId)?.members ?? []).filter(
      (m) => m !== this.#identity.userId,
    );
    await this.#client.sendMessage(
      token,
      conversationId,
      {
        conversationId,
        senderDevice: this.#identity.deviceId,
        sentAt: Date.now(),
        ciphertext: toBase64Url(result.commit),
      },
      peers,
      // MLS handshake commit — not user-facing, no push.
      { silent: true },
    );

    // Refresh our local member roster from the authoritative ratchet
    // tree so the UI lists every member (not just "self + invitee").
    const idsFromTree = memberIdentities(result.newState) as UserId[];
    const existingMeta = this.#meta.get(conversationId) ?? {
      members: [this.#identity.userId],
      updatedAt: 0,
    };
    const merged = Array.from(new Set<UserId>([...existingMeta.members, ...idsFromTree]));
    await this.#persistMeta(conversationId, {
      ...existingMeta,
      members: merged,
      updatedAt: Date.now(),
    });
  }

  /**
   * Remove a member from a conversation. Kicks them out
   * cryptographically (MLS Remove proposal + commit) AND tells the
   * server to strike them from the DO member list so they can't
   * /send into the room. The kicked member's local MLS state still
   * processes the broadcast commit; from that epoch forward they
   * can't decrypt anything new in this room.
   *
   * Refuses to kick self — leaves are a separate `removeConversation`
   * call. Throws if `targetUserId` isn't currently in the ratchet
   * tree.
   */
  async kick(conversationId: ConversationId, targetUserId: UserId): Promise<void> {
    if (targetUserId === this.#identity.userId) {
      throw new Error('cannot kick self — use removeConversation to leave');
    }
    const state = this.#mustHaveState(conversationId);
    const leafIndex = memberLeafIndex(state, targetUserId);
    if (leafIndex < 0) throw new Error(`${targetUserId} is not a member`);
    const result = await removeMember(state, leafIndex, this.#suite);
    this.#states.set(conversationId, result.newState);
    await this.#persistStateOnly(conversationId, result.newState);

    const token = await this.#getToken();
    // Broadcast the commit so every remaining member advances to
    // the new epoch. The kicked user gets the same envelope and
    // processes it as a regular handshake — they advance once and
    // immediately can't decrypt anything from there on.
    const peersBefore = (this.#meta.get(conversationId)?.members ?? []).filter(
      (m) => m !== this.#identity.userId,
    );
    await this.#client.sendMessage(
      token,
      conversationId,
      {
        conversationId,
        senderDevice: this.#identity.deviceId,
        sentAt: Date.now(),
        ciphertext: toBase64Url(result.commit),
      },
      peersBefore,
      // MLS Remove handshake — silent on the wire, not user-facing.
      { silent: true },
    );

    // Strike the kicked user from the DO's server-side member list.
    // After this they can't /send into the room or open a WS for it.
    try {
      await this.#client.removeConversationMember(token, conversationId, targetUserId);
    } catch {
      // Best-effort: the MLS commit alone evicts them cryptographically;
      // the DO record cleanup is hygiene. A retry on the next admin
      // sweep will catch it.
    }

    // Refresh local member roster from the post-commit ratchet tree.
    const idsFromTree = memberIdentities(result.newState) as UserId[];
    const existingMeta = this.#meta.get(conversationId) ?? {
      members: [this.#identity.userId],
      updatedAt: 0,
    };
    await this.#persistMeta(conversationId, {
      ...existingMeta,
      members: idsFromTree,
      updatedAt: Date.now(),
    });
  }

  /**
   * Drain the invitation inbox and join each group. Returns the conversation
   * ids that were joined this call.
   */
  async acceptInvitations(): Promise<ConversationId[]> {
    const token = await this.#getToken();
    const invitations = await this.#client.fetchInvitations(token);
    const joined: ConversationId[] = [];
    for (const inv of invitations) {
      if (this.#states.has(inv.conversationId)) continue;
      // Drop invitations from blocked users. The server already
      // validates from/to; we just refuse to mint local state for
      // a conversation the user explicitly didn't want.
      if (this.#blocked.has(inv.fromUserId)) continue;
      const welcomeBytes = fromBase64Url(inv.welcome);
      const [rtree] = decodeRatchetTree(fromBase64Url(inv.ratchetTree), 0) ?? [undefined];
      if (!rtree) throw new Error('invalid ratchet tree in invitation');
      const { bundle, key } = await this.#tryJoinWithPool(welcomeBytes, rtree);
      const state = await acceptWelcome(welcomeBytes, bundle, rtree, this.#suite);
      this.#states.set(inv.conversationId, state);
      await this.#persist(inv.conversationId, state, bundle);
      await this.#store.delete(key);
      // Derive the full member roster from the Welcome's ratchet
      // tree — every existing leaf's basic-credential identity is a
      // userId in this group. Covers the case where the inviter
      // added other members before us (we wouldn't know about them
      // from the welcome metadata alone).
      const idsFromTree = memberIdentities(state) as UserId[];
      const members = Array.from(
        new Set<UserId>([this.#identity.userId, inv.fromUserId, ...idsFromTree]),
      );
      await this.#persistMeta(inv.conversationId, {
        members,
        updatedAt: Date.now(),
      });
      joined.push(inv.conversationId);
    }
    return joined;
  }

  async send(conversationId: ConversationId, plaintext: Uint8Array): Promise<Uint8Array> {
    const state = this.#mustHaveState(conversationId);
    const result = await encryptMessage(state, plaintext, this.#suite);
    this.#states.set(conversationId, result.newState);
    await this.#persistStateOnly(conversationId, result.newState);
    return result.ciphertext;
  }

  /**
   * Process an inbound envelope. Returns the decrypted plaintext for
   * application messages, or `undefined` when the envelope was a
   * handshake (commit / proposal) — the ClientState still advances
   * and gets persisted either way.
   *
   * When a handshake arrives the local member roster is re-derived
   * from the updated ratchet tree, so a newly-added peer shows up on
   * every existing member's client without any extra signalling.
   */
  async receive(
    conversationId: ConversationId,
    ciphertext: Uint8Array,
  ): Promise<Uint8Array | undefined> {
    const state = this.#mustHaveState(conversationId);
    const result = await decryptMessage(state, ciphertext, this.#suite);
    this.#states.set(conversationId, result.newState);
    await this.#persistStateOnly(conversationId, result.newState);
    if (result.handshake) {
      const existingMeta = this.#meta.get(conversationId) ?? {
        members: [this.#identity.userId],
        updatedAt: 0,
      };
      // The ratchet tree is authoritative AFTER a handshake. Use it
      // verbatim — both add and remove commits update the leaf set,
      // so a merge-only strategy would silently miss a kick.
      const idsFromTree = memberIdentities(result.newState) as UserId[];
      const merged = idsFromTree;
      const changed =
        merged.length !== existingMeta.members.length ||
        merged.some((m, i) => existingMeta.members[i] !== m);
      if (changed) {
        await this.#persistMeta(conversationId, {
          ...existingMeta,
          members: merged,
          updatedAt: Date.now(),
        });
      }
      return undefined;
    }
    return result.plaintext;
  }

  /**
   * Generate `count` fresh KeyPackages, publish their public halves to the
   * server directory, and store the private halves locally keyed by init-key
   * so incoming Welcomes can be matched back to their private material.
   */
  async refillKeyPackages(count: number): Promise<void> {
    const token = await this.#getToken();
    const bundles: KeyPackageBundle[] = [];
    for (let i = 0; i < count; i++) {
      bundles.push(await createKeyPackage(this.#identity.userId, this.#suite));
    }
    const baseSequence = Date.now();
    const published: PublishedKeyPackage[] = bundles.map((bundle, index) => ({
      userId: this.#identity.userId,
      deviceId: this.#identity.deviceId,
      keyPackage: encodeKeyPackage(bundle),
      sequence: baseSequence + index,
    }));
    for (const [i, bundle] of bundles.entries()) {
      const initKey = toBase64Url(bundle.publicPackage.initKey);
      const serialized = JSON.stringify({
        keyPackage: encodeKeyPackage(bundle),
        privatePackage: encodePrivatePackage(bundle.privatePackage),
        sequence: baseSequence + i,
      });
      await this.#store.put(`chat:kp-pool:v1:${initKey}`, new TextEncoder().encode(serialized));
    }
    try {
      await this.#client.publishKeyPackages(token, published);
    } catch (err) {
      // A "key package pool full" 429 is expected on every login once
      // a returning user has accumulated close to the 100-KP ceiling.
      // It means the server already has plenty; nothing for us to do
      // and nothing the user should see as an error. The local
      // private halves we just persisted stay around harmlessly —
      // future Welcomes find their match by init key regardless of
      // whether the public half was published this round.
      const msg = err instanceof Error ? err.message : '';
      if (/pool full/i.test(msg)) return;
      throw err;
    }
  }

  /**
   * Produce an initial DeviceRegistration payload for the server.
   */
  buildDeviceRegistration(): DeviceRegistration {
    return {
      userId: this.#identity.userId,
      deviceId: this.#identity.deviceId,
      // Under protocol v2 the directory's identityPublicKey field
      // holds the long-term identity key (the one that derives userId
      // and signs claim/rotate-auth bindings), not the rotatable auth
      // key. Matches server's email:<...> binding stored at claim time.
      identityPublicKey: toBase64Url(this.#identity.identityPublicKey),
      registeredAt: Date.now(),
    };
  }

  #mustHaveState(id: ConversationId): ClientState {
    const state = this.#states.get(id);
    if (!state) throw new Error(`no conversation ${id}`);
    return state;
  }

  async #persist(
    conversationId: ConversationId,
    state: ClientState,
    bundle: KeyPackageBundle,
  ): Promise<void> {
    const persisted: PersistedConversation = {
      groupId: toBase64Url(state.groupContext.groupId),
      conversationId,
      groupState: toBase64Url(encodeGroupState(state)),
      keyPackage: encodeKeyPackage(bundle),
      privatePackage: encodePrivatePackage(bundle.privatePackage),
    };
    await this.#store.put(
      stateKey(conversationId),
      new TextEncoder().encode(JSON.stringify(persisted)),
    );
    void keyPackageCacheKey; // reserved for future per-epoch rotation
  }

  async #persistStateOnly(conversationId: ConversationId, state: ClientState): Promise<void> {
    const existing = await this.#store.get(stateKey(conversationId));
    if (!existing) return;
    const parsed = JSON.parse(new TextDecoder().decode(existing)) as PersistedConversation;
    parsed.groupState = toBase64Url(encodeGroupState(state));
    await this.#store.put(
      stateKey(conversationId),
      new TextEncoder().encode(JSON.stringify(parsed)),
    );
  }

  /**
   * For incoming Welcomes we need to match the encrypted init key against a
   * KeyPackage whose private material we retain locally. We try each bundle
   * in the pool in turn — the one `acceptWelcome` succeeds with is the match.
   */
  async #tryJoinWithPool(
    welcomeBytes: Uint8Array,
    ratchetTree: RatchetTree,
  ): Promise<{ bundle: KeyPackageBundle; key: string }> {
    const keys = await this.#store.list('chat:kp-pool:v1:');
    let lastError: unknown;
    for (const key of keys) {
      const raw = await this.#store.get(key);
      if (!raw) continue;
      const parsed = JSON.parse(new TextDecoder().decode(raw)) as {
        keyPackage: string;
        privatePackage: PersistedConversation['privatePackage'];
      };
      const bundle = decodeKeyPackageBundle(parsed.keyPackage, parsed.privatePackage);
      try {
        await acceptWelcome(welcomeBytes, bundle, ratchetTree, this.#suite);
        return { bundle, key };
      } catch (err) {
        lastError = err;
      }
    }
    throw new Error(
      `no matching key package in local pool${lastError instanceof Error ? `: ${lastError.message}` : ''}`,
    );
  }
}
